// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module EventTable
 * @package @geenius/analytics/react-css
 * @description Renders the React CSS recent-events table for analytics
 * dashboards. The component formats event metadata into a compact grid and
 * includes loading skeletons plus an empty state for low-traffic views.
 */

import type { AnalyticsEvent, DeviceInfo, EventType } from "@geenius/analytics";
import {
  Badge,
  Card,
  CardContent,
  EmptyState,
  Skeleton,
} from "@geenius/ui/react-css";
import type React from "react";
import { Fragment, useMemo, useState } from "react";
import { useAnalyticsCopy } from "../lib/analyticsCopy";
import { cx } from "../lib/cx";
import { type AnalyticsErrorStateValue, ErrorState } from "./ErrorState";

type EventTableSortKey = "name" | "type" | "url" | "device" | "timestamp";
type EventTableSortDirection = "asc" | "desc";
type EventTableFilterType = EventType | "all";
type EventTableFilterDevice = DeviceInfo["device"] | "all";

interface EventTableSortState {
  key: EventTableSortKey;
  direction: EventTableSortDirection;
}

interface EventTableFilterState {
  query: string;
  type: EventTableFilterType;
  device: EventTableFilterDevice;
}

const EVENT_TYPES: EventType[] = [
  "custom",
  "page_view",
  "click",
  "form_submit",
];

const DEVICE_TYPES: Array<DeviceInfo["device"]> = [
  "desktop",
  "tablet",
  "mobile",
];

const EVENT_TYPE_LABELS: Record<EventType, string> = {
  click: "analytics.events.types.click",
  custom: "analytics.events.types.custom",
  form_submit: "analytics.events.types.formSubmit",
  page_view: "analytics.events.types.pageView",
};

function formatEventPathname(url: string): string {
  try {
    return new URL(url, "https://geenius.dev").pathname;
  } catch {
    return url;
  }
}

function stringifyEventProperties(properties: Record<string, unknown>): string {
  try {
    return JSON.stringify(properties, null, 2);
  } catch {
    return String(properties);
  }
}

function getSearchText(event: AnalyticsEvent): string {
  return [
    event.name,
    event.type,
    event.url,
    event.referrer,
    event.sessionId,
    event.userId ?? "",
    event.device.device,
    event.device.browser,
    event.device.os,
    stringifyEventProperties(event.properties ?? {}),
  ]
    .join(" ")
    .toLowerCase();
}

function eventMatchesFilters(
  event: AnalyticsEvent,
  filters: EventTableFilterState,
): boolean {
  if (filters.type !== "all" && event.type !== filters.type) return false;
  if (filters.device !== "all" && event.device.device !== filters.device)
    return false;

  const query = filters.query.trim().toLowerCase();
  return query ? getSearchText(event).includes(query) : true;
}

function getSortValue(
  event: AnalyticsEvent,
  key: EventTableSortKey,
): string | number {
  if (key === "device") return event.device.device;
  if (key === "timestamp") return event.timestamp;
  return event[key];
}

function sortEvents(
  events: AnalyticsEvent[],
  sort: EventTableSortState | null,
): AnalyticsEvent[] {
  if (!sort) return events;

  const direction = sort.direction === "asc" ? 1 : -1;
  return [...events].sort((left, right) => {
    const leftValue = getSortValue(left, sort.key);
    const rightValue = getSortValue(right, sort.key);

    if (typeof leftValue === "number" && typeof rightValue === "number") {
      return (leftValue - rightValue) * direction;
    }

    return String(leftValue).localeCompare(String(rightValue)) * direction;
  });
}

function nextSortState(
  current: EventTableSortState | null,
  key: EventTableSortKey,
): EventTableSortState | null {
  if (current?.key !== key) {
    return { key, direction: key === "timestamp" ? "desc" : "asc" };
  }

  if (current.direction === "asc") {
    return { key, direction: "desc" };
  }

  return null;
}

function getAriaSort(
  sort: EventTableSortState | null,
  key: EventTableSortKey,
): React.AriaAttributes["aria-sort"] {
  if (sort?.key !== key) return "none";
  return sort.direction === "asc" ? "ascending" : "descending";
}

/**
 * Props for rendering the React CSS analytics event table.
 *
 * @property events Event rows to display in the table.
 * @property isLoading Whether the table should render loading skeleton rows.
 * @property maxRows Maximum number of event rows to render.
 * @property showControls Whether filter and sort controls should render.
 * @property className Optional class name merged into the wrapper.
 */
export interface EventTableProps {
  events: AnalyticsEvent[];
  isLoading?: boolean;
  error?: AnalyticsErrorStateValue;
  maxRows?: number;
  onRetry?: () => void;
  showControls?: boolean;
  className?: string;
}

/**
 * Renders the React CSS analytics event table.
 *
 * @param props Event rows, loading state, and row limit for the table.
 * @returns An event table element with loading and empty states.
 */
export function EventTable({
  error,
  events,
  isLoading,
  maxRows = 10,
  onRetry,
  showControls = true,
  className,
}: EventTableProps): React.ReactElement {
  const copy = useAnalyticsCopy();
  const [filters, setFilters] = useState<EventTableFilterState>({
    device: "all",
    query: "",
    type: "all",
  });
  const [sort, setSort] = useState<EventTableSortState | null>(null);
  const [expandedEventIds, setExpandedEventIds] = useState<Set<string>>(
    () => new Set(),
  );
  const displayed = useMemo(() => {
    const filtered = events.filter((event) =>
      eventMatchesFilters(event, filters),
    );
    return sortEvents(filtered, sort).slice(0, maxRows);
  }, [events, filters, maxRows, sort]);

  const toggleEventProperties = (eventId: string): void => {
    setExpandedEventIds((current) => {
      const next = new Set(current);
      if (next.has(eventId)) {
        next.delete(eventId);
      } else {
        next.add(eventId);
      }
      return next;
    });
  };

  const renderSortableHeader = (
    key: EventTableSortKey,
    label: string,
  ): React.ReactElement => {
    const active = sort?.key === key;
    const directionLabel =
      active && sort.direction === "asc" ? "ascending" : "descending";

    return (
      <th scope="col" aria-sort={getAriaSort(sort, key)}>
        <button
          className={cx(
            "gn-analytics-event-table__sort-button",
            active && "gn-analytics-event-table__sort-button--active",
          )}
          type="button"
          onClick={() => setSort((current) => nextSortState(current, key))}
        >
          <span>{label}</span>
          <span
            className="gn-analytics-event-table__sort-indicator"
            aria-hidden="true"
          >
            {active ? directionLabel : "sort"}
          </span>
          <span className="gn-analytics-a11y__sr-only">
            {active
              ? `Sorted ${directionLabel}; activate to change sorting`
              : `Sort by ${label}`}
          </span>
        </button>
      </th>
    );
  };

  if (error) {
    return (
      <Card className={className}>
        <CardContent className="gn-analytics-event-table">
          <ErrorState error={error} onRetry={onRetry} />
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card className={className}>
        <CardContent className="gn-analytics-event-table">
          <table
            className="gn-analytics-event-table__table"
            data-analytics-table-loading
            aria-busy="true"
            aria-label={copy.t("analytics.dashboard.recentEvents")}
          >
            <tbody>
              {Array.from(
                { length: 5 },
                (_, index) => `event-table-skeleton-${index}`,
              ).map((skeletonId) => (
                <tr key={skeletonId}>
                  <td
                    className="gn-analytics-event-table__loading-cell"
                    colSpan={5}
                  >
                    <Skeleton height={40} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardContent className="gn-analytics-event-table">
        {showControls ? (
          <div
            className="gn-analytics-event-table__controls"
            data-analytics-table-controls
          >
            <label className="gn-analytics-event-table__filter">
              <span>{copy.t("analytics.events.filters.search")}</span>
              <input
                className="gn-analytics-event-table__input"
                type="search"
                value={filters.query}
                placeholder={copy.t(
                  "analytics.events.filters.searchPlaceholder",
                )}
                onChange={(event) => {
                  const query = event.currentTarget.value;
                  setFilters((current) => ({
                    ...current,
                    query,
                  }));
                }}
              />
            </label>
            <label className="gn-analytics-event-table__filter">
              <span>{copy.t("analytics.events.filters.type")}</span>
              <select
                className="gn-analytics-event-table__select"
                value={filters.type}
                onChange={(event) => {
                  const type = event.currentTarget
                    .value as EventTableFilterType;
                  setFilters((current) => ({
                    ...current,
                    type,
                  }));
                }}
              >
                <option value="all">
                  {copy.t("analytics.events.filters.allTypes")}
                </option>
                {EVENT_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {copy.t(EVENT_TYPE_LABELS[type])}
                  </option>
                ))}
              </select>
            </label>
            <label className="gn-analytics-event-table__filter">
              <span>{copy.t("analytics.events.filters.device")}</span>
              <select
                className="gn-analytics-event-table__select"
                value={filters.device}
                onChange={(event) => {
                  const device = event.currentTarget
                    .value as EventTableFilterDevice;
                  setFilters((current) => ({
                    ...current,
                    device,
                  }));
                }}
              >
                <option value="all">
                  {copy.t("analytics.events.filters.allDevices")}
                </option>
                {DEVICE_TYPES.map((device) => (
                  <option key={device} value={device}>
                    {copy.deviceLabel(device)}
                  </option>
                ))}
              </select>
            </label>
          </div>
        ) : null}
        {displayed.length === 0 ? (
          <EmptyState title={copy.t("analytics.events.empty")} />
        ) : (
          <table
            className="gn-analytics-event-table__table"
            data-analytics-table
            aria-label={copy.t("analytics.dashboard.recentEvents")}
          >
            <thead>
              <tr data-analytics-table-header>
                {renderSortableHeader(
                  "name",
                  copy.t("analytics.events.columns.event"),
                )}
                {renderSortableHeader(
                  "type",
                  copy.t("analytics.events.columns.type"),
                )}
                {renderSortableHeader(
                  "url",
                  copy.t("analytics.events.columns.url"),
                )}
                {renderSortableHeader(
                  "device",
                  copy.t("analytics.events.columns.device"),
                )}
                {renderSortableHeader(
                  "timestamp",
                  copy.t("analytics.events.columns.time"),
                )}
              </tr>
            </thead>
            <tbody>
              {displayed.map((event) => {
                const hasProperties =
                  Object.keys(event.properties ?? {}).length > 0;
                const isExpanded = expandedEventIds.has(event.id);
                const propertiesId = `gn-analytics-event-properties-${event.id}`;

                return (
                  <Fragment key={event.id}>
                    <tr data-analytics-table-row>
                      <td
                        className="gn-analytics-event-table__event-cell"
                        data-analytics-cell-name
                      >
                        <span className="gn-analytics-event-table__event-name">
                          {event.name}
                        </span>
                        {hasProperties ? (
                          <button
                            className="gn-analytics-event-table__properties-button"
                            type="button"
                            aria-controls={propertiesId}
                            aria-expanded={isExpanded}
                            aria-label={`${isExpanded ? "Hide" : "Show"} properties for ${event.name}`}
                            onClick={() => toggleEventProperties(event.id)}
                          >
                            {isExpanded ? "Hide properties" : "Show properties"}
                          </button>
                        ) : null}
                      </td>
                      <td>
                        <Badge size="sm" variant="secondary">
                          <span data-analytics-cell-type data-type={event.type}>
                            {event.type}
                          </span>
                        </Badge>
                      </td>
                      <td data-analytics-cell-url title={event.url}>
                        {formatEventPathname(event.url)}
                      </td>
                      <td
                        data-analytics-cell-device
                        data-device={event.device.device}
                      >
                        <span aria-hidden="true">
                          {event.device.device === "mobile"
                            ? "📱"
                            : event.device.device === "tablet"
                              ? "📟"
                              : "💻"}{" "}
                        </span>
                        {event.device.browser}
                      </td>
                      <td data-analytics-cell-time>
                        {copy.formatTime(event.timestamp)}
                      </td>
                    </tr>
                    {hasProperties && isExpanded ? (
                      <tr
                        className="gn-analytics-event-table__properties-row"
                        data-analytics-table-properties
                        id={propertiesId}
                      >
                        <td
                          className="gn-analytics-event-table__properties-cell"
                          colSpan={5}
                        >
                          <pre className="gn-analytics-event-table__properties">
                            {stringifyEventProperties(event.properties ?? {})}
                          </pre>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}
